import { User } from '../../core/user/users.types';
import { HapticFeedback, NativeShareOptions, NativeUserLocation } from './native.types';

const NativeDevice = window.ReactNativeWebView;

/** Helper type to extract required keys from NativeActionArgs['data'] */
type RequiredDataKey<K extends keyof NonNullable<NativeActionArgs['data']>> = {
  data: Required<Pick<NonNullable<NativeActionArgs['data']>, K>>;
};

/** Helper type to create a discriminated union of native actions and their required arguments */
type NativePostAction<
  TAction extends keyof NativePostActionArgList = keyof NativePostActionArgList,
  TActionArgs extends NativePostActionArgList[TAction] = NativePostActionArgList[TAction],
> = (action: TAction, args?: TActionArgs) => void;

/** All arguments that can be passed to a native action */
type NativeActionArgs = {
  // TODO: Remove the 'object' type once we deprecate the 'user' and 'session' actions.
  data?: object & {
    session?: User['session'];
    share_options?: NativeShareOptions;
    /** Generic URL that will be used for the relevant requests */
    url?: string;
    share_cause_options?: {
      url: string;
      cause_name: string;
    };
  };

  // TODO: Move these to the NativeActionArgs type as we deprecate older native actions.
  url?: string;
  cause_url?: string;
  userLocation?: NativeUserLocation;
  desiredLocation?: NativeUserLocation;
  feedback?: HapticFeedback;
  queryKey?: string[] | ReadonlyArray<string>;
  // TODO: Deprecate the below after native version Givebacks 3.5.2 is released.
  user?: User;
};

/** All native actions that can be called from the webview and their required arguments */
type NativePostActionArgList = {
  /** Updates the user session */
  user_session: RequiredDataKey<'session'>;
  /** Triggers a haptic feedback */
  hapticFeedback: Pick<NativeActionArgs, 'feedback'>;
  /** Opens the directions screen */
  directions: Pick<NativeActionArgs, 'userLocation'> & Pick<NativeActionArgs, 'desiredLocation'>;
  /** Opens the share sheet */
  share: RequiredDataKey<'share_options'>;
  /** Opens the share sheet for a cause */
  share_cause: RequiredDataKey<'share_cause_options'>;
  /** Invalidates a query key */
  invalidate_query_key: Pick<NativeActionArgs, 'queryKey'>;
  /** Opens a given URL */
  'URL.open': Pick<NativeActionArgs, 'url'>;
  /** Navigates to a given URL */
  navigate: Pick<NativeActionArgs, 'url'>;
  /** Updates the user
   * @deprecated Use `user_session` instead.
   */
  user: Pick<NativeActionArgs, 'user'>;
};

/** Simple messages sent to the native device */
type NativeMessage =
  | 'Logout'
  | 'Notifications'
  | 'getCurrentLocation'
  | 'StartExtensionInstallation';

/** Simple wrapper to catch errors  and validate native device  exists before calling postMessage*/
const postNativeMessage = (message: string) => {
  try {
    if (NativeDevice) {
      void NativeDevice.postMessage(message);
    }
  } catch {}
};

/** Calls the native action */
const postAction: NativePostAction = (
  action: keyof NativePostActionArgList,
  args?: NativePostActionArgList[typeof action]
) => {
  try {
    postNativeMessage(JSON.stringify({ action, ...args }));
  } catch {}
};

/** Calls the native haptic feedback */
const haptic = (feedback: HapticFeedback) => {
  postAction('hapticFeedback', { feedback });
};

function postMessage(message: NativeMessage) {
  try {
    postNativeMessage(message);
  } catch {}
}

function postURL(url: string) {
  try {
    postNativeMessage(JSON.stringify({ url: url, action: 'URL.open' }));
  } catch {}
}

function postShareURL(url: string) {
  try {
    postNativeMessage(JSON.stringify({ url: url, action: 'ShareURL' }));
  } catch {}
}

const hasWebView = () => {
  return NativeDevice ? true : false;
};

/** Requests the native app to navigate to a given path*/
const navigateTo = (url: string) => {
  try {
    postNativeMessage(JSON.stringify({ url, action: 'navigate' }));
  } catch {}
};

const appVersion = window?.nativeApp?.appVersion;
const contactSeller = window?.nativeApp?.contactSeller ?? false;

export const Native = () => {};
Native.postAction = postAction;
Native.postMessage = postMessage;
Native.postURL = postURL;

Native.postShareURL = postShareURL;
Native.hasWebView = hasWebView;
Native.WebView = NativeDevice;
Native.haptic = haptic;
Native.appVersion = appVersion;
Native.hasContactSeller = contactSeller;

Native.navigateTo = navigateTo;
export type { NativePostActionArgList };
